'use client';

import type { ReactNode } from 'react';
import { Modal } from '@/app/components/Modal';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { cn } from '@/utils/сn';
import { confirmModalActionStyles, confirmModalPanelStyles } from './styles';

type ConfirmModalIntent = 'primary' | 'secondary' | 'danger';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  confirmLabel: ReactNode;
  description?: ReactNode;
  cancelLabel?: ReactNode;
  confirmIntent?: ConfirmModalIntent;
  cancelIntent?: ConfirmModalIntent;
  confirmIcon?: ReactNode;
  icon?: ReactNode;
  isPending?: boolean;
  hideCancel?: boolean;
  panelClassName?: string;
  testId?: string;
}

export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  confirmLabel,
  description,
  cancelLabel = 'Cancel',
  confirmIntent = 'danger',
  cancelIntent = 'secondary',
  confirmIcon,
  icon,
  isPending = false,
  hideCancel = false,
  panelClassName,
  testId,
}: ConfirmModalProps) => (
  <Modal open={isOpen} onClose={onClose}>
    <div className={cn(confirmModalPanelStyles(), panelClassName)} data-testid={testId}>
      <div className="flex flex-col items-center gap-5 text-center">
        {icon}
        <div className="flex flex-col items-center gap-1.5">
          <h2 className="light text-blue-900 max-md:px-8">{title}</h2>
          {description ? (
            <p className="text-body-regular text-grey-700 max-md:text-sm max-md:leading-[150%]">{description}</p>
          ) : null}
        </div>
      </div>

      <div className="flex w-full gap-5 max-md:gap-4">
        {hideCancel ? null : (
          <BaseButton
            type="button"
            intent={cancelIntent}
            size="full"
            onClick={onClose}
            disabled={isPending}
            data-testid={testId ? `${testId}-cancel` : undefined}
            className={confirmModalActionStyles()}
          >
            {cancelLabel}
          </BaseButton>
        )}
        <BaseButton
          type="button"
          intent={confirmIntent}
          size="full"
          onClick={onConfirm}
          disabled={isPending}
          startIcon={confirmIcon}
          data-testid={testId ? `${testId}-confirm` : undefined}
          className={confirmModalActionStyles()}
        >
          {confirmLabel}
        </BaseButton>
      </div>
    </div>
  </Modal>
);
